import { Theme } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/styles";
import React, { useEffect, useState } from "react";
import AphDialog from "../web-ui-components/AphDialog";
import AphDialogClose from "../web-ui-components/AphDialogClose";
import AphDialogTitle from "../web-ui-components/AphDialogTitle";
import { useDiagnosticsCart } from "./DiagnosticsCartProvider";
import { useGlobalDiagnosticsCart } from "./DiagnosticsCartProviderGlobal";
import { Alerts } from "components/Alerts/Alerts";
import { TestLocation } from "components/Tests/TestLocation";
import { GET_DIAGNOSTIC_SERVICEABILITY } from "graphql/diagnostics";
import {
  getDiagnosticServiceability,
  getDiagnosticServiceabilityVariables,
} from "graphql/types/getDiagnosticServiceability";
import { isActualUser, isBrowser } from "helpers/commonHelpers";
import { useApolloClient } from "react-apollo-hooks";
import { SOMETHING_WENT_WRONG } from "strings/AppConfig";
import axios from "axios";
import { Helmet } from "react-helmet";
import { DiagnosticsBookingSource } from "graphql/types/globalTypes";
import { dataLayerTracking } from "gtmTracking";

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    userLocation: {
      borderLeft: "0.5px solid rgba(2,71,91,0.3)",
      padding: "10px 20px",
      position: "relative",
      [theme.breakpoints.down("xs")]: {
        borderLeft: "none",
        padding: "0px 10px 0px 20px",
      },
    },
    locationWrap: {
      maxWidth: 210,
      cursor: "pointer",
      [theme.breakpoints.down("xs")]: {
        maxWidth: 180,
      },
      [theme.breakpoints.down(500)]: {
        maxWidth: 150,
      },
    },
    selectedLocation: {
      fontSize: 14,
      fontWeight: 500,
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      display: "flex",
      alignItems: "center",
      "& div": {
        borderBottom: "2px solid #00b38e",
      },
      "& span:first-child": {
        height: "22px",
        marginRight: "10px",
      },
      "& span:last-child": {
        "& img": {
          verticalAlign: "middle",
        },
      },
      [theme.breakpoints.down("xs")]: {
        marginLeft: "auto",
      },
      [theme.breakpoints.down(360)]: {
        display: "none",
      },
    },
    userName: {
      fontSize: 12,
      color: "#01475b",
      lineHeight: "normal",
    },
    locationPopRoot: {
      left: "21% !important",
      top: "66px !important",
      overflow: "initial",
      boxShadow: "0 0 3px 0 rgba(0, 0, 0, 0.2)",
      border: "solid 1px #f7f8f5",
      "& ul": {
        margin: 0,
        padding: 0,
        "& li": {
          padding: "12px 20px",
          fontSize: 15,
          fontWeight: 500,
          borderBottom: "solid 1px #f7f8f5",
          listStyleType: "none",
          cursor: "pointer",
          "&:last-child": {
            borderBottom: "none",
          },
        },
      },
    },
    noService: {
      fontSize: 12,
      color: "#890000",
      position: "absolute",
      paddingTop: 5,
      width: 215,
    },
    service: {
      fontSize: 11,
      color: "#000000",
      position: "absolute",
      paddingTop: 5,
      width: 215,
    },
    pincodeData: {
      padding: 30,
      fontSize: 14,
      color: "#02475b",
      fontWeight: 500,
      "& h2": {
        fontSize: 32,
        lineHeight: "42px",
        fontWeight: 600,
        margin: 0,
      },
    },
    bottomActions: {
      textAlign: "center",
      paddingTop: 16,
    },
    submitBtn: {
      backgroundColor: "#fcb716",
      color: "#fff",
      minWidth: 150,
      borderRadius: 10,
      padding: "9px 13px 9px 13px",
      fontSize: 13,
      fontWeight: "bold",
      "&:hover": {
        backgroundColor: "#fcb716",
        color: "#fff",
      },
    },
    disabledBtn: {
      opacity: 0.6,
    },
    pincodeError: {
      fontSize: 13,
      color: "#890000",
      paddingTop: 8,
      fontWeight: 500,
    },
    bottomPopover: {
      overflow: "initial",
      backgroundColor: "transparent",
      boxShadow: "none",
    },
    successPopoverWindow: {
      display: "flex",
      marginRight: 5,
      marginBottom: 5,
      [theme.breakpoints.down("xs")]: {
        marginLeft: 5,
      },
    },
    windowWrap: {
      width: 368,
      borderRadius: 10,
      paddingTop: 36,
      boxShadow: "0 5px 40px 0 rgba(0, 0, 0, 0.3)",
      backgroundColor: theme.palette.common.white,
    },
    mascotIcon: {
      position: "absolute",
      right: 12,
      top: -40,
      "& img": {
        maxWidth: 72,
      },
    },
    locationPopoverClose: {
      position: "absolute",
      top: -10,
      height: 26,
      borderRadius: "50%",
      boxShadow: "1px 1px 2px 1px #eaeaea",
      left: -10,
      cursor: "pointer",
      [theme.breakpoints.down("xs")]: {
        left: "0px !important",
      },
    },
    popOver: {
      "&>div:first-child": {
        background: "rgba(0,0,0,0.5) !important",
      },
    },
    locationTitle: {
      padding: 16,
      "& h2": {
        fontSize: 13,
        fontweight: 600,
      },
    },
  });
});

export const TestsPincodeSearch: React.FC = () => {
  const [isAlertOpen, setIsAlertOpen] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const classes = useStyles({});
  const client = useApolloClient();
  const [isUserDeniedLocationAccess, setIsUserDeniedLocationAccess] = useState<
    boolean | null
  >(null);
  const {
    fallbackAreaName,
    setFallbackAreaName,
    setCartCityId,
    openLocation,
    setOpenLocation,
    showLocationPopup,
    setShowLocationPopup,
    ctaConfig,
  } = useDiagnosticsCart();
  const {
    diagnosticAddress,
    setDiagnosticAddress,
    isAreaServiceable,
    setServiceability,
  } = useGlobalDiagnosticsCart();
  const isUser = isActualUser();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFetchingLocation, setIsFetchingLocation] = useState<boolean>(false);
  const [
    updateToDefaultLocation,
    setUpdateToDefaultLocation,
  ] = useState<boolean>(false);

  const getDataFromGoogleMaps = (
    latitude: number,
    longitude: number,
    serviceable: boolean
  ) => {
    let zipcode = "";
    axios
      .get(
        `${process.env.NEXT_PUBLIC_GOOGLE_MAP_API}?latlng=${latitude},${longitude}&key=${process.env.NEXT_PUBLIC_GOOGLE_MAP_GEOCODE_API_KEY}`
      )
      .then(({ data }) => {
        const adminAreaLvl2 = data.results.find((addResult: any) =>
          addResult.address_components.some((addrComponent: any) => {
            return addrComponent.types.includes("administrative_area_level_2");
          })
        );
        let areaLocality = "";
        if (!adminAreaLvl2) {
          areaLocality = data.results.find((addResult: any) =>
            addResult.address_components.some((addrComponent: any) => {
              return addrComponent.types.includes("locality");
            })
          );
        }
        const areaToUse = areaLocality !== "" ? areaLocality : adminAreaLvl2;
        const areaName =
          areaToUse?.address_components &&
          areaToUse?.address_components.length &&
          areaToUse?.address_components.find((addComp: any) =>
            addComp.types.includes(
              !!areaLocality ? "locality" : "administrative_area_level_2"
            )
          ).long_name;
        areaName && setFallbackAreaName(areaName);
        const addComp = data.results.find((addResult: any) =>
          addResult.address_components.some((addrComponent: any) => {
            return addrComponent.types.includes("postal_code");
          })
        );
        zipcode = addComp?.address_components.find((addrComponent: any) => {
          return addrComponent.types.includes("postal_code");
        }).long_name;
      });
  };

  useEffect(() => {
    if (!isAreaServiceable && fallbackAreaName) {
      const diagAddress = {
        cityID: 0,
        cityName: fallbackAreaName,
        stateID: 0,
        stateName: "",
      };
      setCartCityId(null);
      setDiagnosticAddress(diagAddress);
    }
  }, [fallbackAreaName, isAreaServiceable]);

  const locateCurrentLocation = () => {
    setIsLoading(true);
    setIsFetchingLocation(true);
    let serviceable = false;
    navigator.geolocation.getCurrentPosition(
      ({ coords: { latitude, longitude } }) => {
        setIsUserDeniedLocationAccess(false);
        setCartCityId(null);
        setDiagnosticAddress(null);
        setFallbackAreaName("");
        client
          .query<
            getDiagnosticServiceability,
            getDiagnosticServiceabilityVariables
          >({
            query: GET_DIAGNOSTIC_SERVICEABILITY,
            variables: {
              longitude,
              latitude,
              source: DiagnosticsBookingSource.WEB,
            },
          })
          .then(({ data }) => {
            if (data?.getDiagnosticServiceability?.status) {
              dataLayerTracking({
                event: "location_selected",
                patient_location:
                  data?.getDiagnosticServiceability?.serviceability?.city +
                  "_" +
                  data?.getDiagnosticServiceability?.serviceability?.state,
                state: data?.getDiagnosticServiceability?.serviceability?.state,
                section: "Lab Tests",
              });
              const serviceabilityData =
                data.getDiagnosticServiceability.serviceability;
              const diagAddress = {
                cityID: serviceabilityData?.cityID || 0,
                cityName: serviceabilityData?.city || "",
                stateID: serviceabilityData?.stateID || 0,
                stateName: serviceabilityData?.state || "",
                latitude,
                longitude,
              };
              setCartCityId(diagAddress.cityID);
              setDiagnosticAddress(diagAddress);
              setServiceability(true);
              serviceable = true;
            } else {
              setServiceability(false);
              serviceable = false;
            }
          })
          .catch(() => {
            setIsAlertOpen(true);
            setAlertMessage(SOMETHING_WENT_WRONG);
          })
          .finally(() => {
            setShowLocationPopup(false);
            setIsLoading(false);
            setIsFetchingLocation(false);
            getDataFromGoogleMaps(latitude, longitude, serviceable);
          });
      },
      (err) => {
        setIsFetchingLocation(false);
        setIsLoading(false);
        setIsUserDeniedLocationAccess(true);
        console.log(err.message);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      }
    );
  };

  useEffect(() => {
    if (
      isBrowser() &&
      !localStorage.getItem("diagnosticAddress") &&
      !localStorage.getItem("pharmaPincode") &&
      !isUserDeniedLocationAccess
    ) {
      navigator?.permissions &&
        navigator.permissions
          .query({ name: "geolocation" })
          .then((PermissionStatus) => {
            if (PermissionStatus.state === "denied") {
              setIsUserDeniedLocationAccess(true);
            } else if (
              PermissionStatus.state !== "granted" ||
              PermissionStatus.state === "granted"
            ) {
              locateCurrentLocation();
            }
          });
    } else {
      if (
        (diagnosticAddress && diagnosticAddress.cityID) ||
        localStorage.getItem("pharmaPincode")
      ) {
        if (localStorage.getItem("pharmaPincode")) {
          // isServiceable(Number(localStorage.getItem('pharmaPincode')), 'auto');
        } else {
          // isServiceable(diagnosticAddress.pinCode, 'auto');
        }
      }
    }
  }, []);

  useEffect(() => {
    if (openLocation) {
      setShowLocationPopup(true);
    }
  }, [openLocation]);

  const dialogCloseHandler = () => {
    setShowLocationPopup(false);
    setOpenLocation(false);
  };

  return (
    <div className={classes.userLocation}>
      <Helmet>
        <script
          src={`https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY}&libraries=places`}
        />
      </Helmet>
      <div
        className={classes.locationWrap}
        onClick={() => setShowLocationPopup(true)}
        id="locationId"
      >
        <div className={classes.selectedLocation}>
          <span>
            <img
              src={`${process.env.NEXT_PUBLIC_247_CDN_URL}/images/location-icon.svg`}
              alt="location-img"
            />
          </span>
          <>{`${diagnosticAddress?.cityName || "No Location"}`}</>
        </div>

        <div
          className={`${
            isAreaServiceable ? classes.service : classes.noService
          }`}
        >
          {diagnosticAddress?.cityName
            ? isAreaServiceable
              ? ctaConfig?.servicabilityTextConfig?.servicable
              : ctaConfig?.servicabilityTextConfig?.nonServicable
            : ctaConfig?.servicabilityTextConfig?.noLocation}
        </div>
      </div>

      {/* location popup */}
      {typeof window !== "undefined" &&
        window.navigator &&
        isUser &&
        showLocationPopup && (
          <AphDialog open={showLocationPopup} maxWidth="sm">
            <AphDialogClose onClick={dialogCloseHandler} title={"Close"} />
            <AphDialogTitle className={classes.locationTitle}>
              SAMPLE COLLECTION LOCATION
            </AphDialogTitle>
            <TestLocation
              locateCurrentLocation={locateCurrentLocation}
              isUserDeniedLocationAccess={isUserDeniedLocationAccess}
              setIsUserDeniedLocationAccess={setIsUserDeniedLocationAccess}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              isFetchingLocation={isFetchingLocation}
              updateToDefaultLocation={updateToDefaultLocation}
              setUpdateToDefaultLocation={setUpdateToDefaultLocation}
            />
          </AphDialog>
        )}

      <Alerts
        setAlertMessage={setAlertMessage}
        alertMessage={alertMessage}
        isAlertOpen={isAlertOpen}
        setIsAlertOpen={setIsAlertOpen}
      />
    </div>
  );
};
